import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Textarea } from '@/components/ui/textarea.jsx';
import { CHOFERES_LIST } from '@/graphql/choferes.gql.js';
import { RETORNABLES_EN_CIRCULACION } from '@/graphql/retornables.gql.js';
import { fetchVehiculos } from '@/pages/Administracion/Flota/services/flota.service.js';
import {
  APROBAR_RENDICION,
  ASIGNAR_GUIAS_REPARTO,
  CERRAR_REPARTO,
  GENERAR_LIQUIDACION,
  INICIAR_REPARTO,
  MARCAR_LIQUIDACION_PAGADA,
  REGISTRAR_ENTREGA,
  REGISTRAR_RENDICION,
  REGISTRAR_RETENCION,
  REPARTO_DETALLE,
} from '@/graphql/repartos.gql.js';
import { openRepartoPlanilla } from '@/services/repartos.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { hasPermission } from '@/utils/permissions.js';
import AgregarGuiasModal from './AgregarGuiasModal.jsx';
import { RETIRO_BLOQUEADO_MENSAJE, canActOnGuia, canAddGuias, getRepartoActions, guiaActionBlockReason } from './repartoActions.js';
import { calcDiferencia, calcTotalFinal, validateLiquidacion, validateRendicion } from './repartoMoney.js';

const formatCurrency = value => `$ ${Number(value || 0).toFixed(2)}`;
const formatDate = value => (value ? String(value).slice(0, 10) : '-');

export default function RepartoDetalle() {
  const { id } = useParams();
  const repartoId = Number(id);
  const { sucursal, user } = useAuth();
  const receptorRef = useRef(null);

  const [tab, setTab] = useState('guias');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null); // { type: 'entrega' | 'retencion', guia }
  const [actionText, setActionText] = useState('');
  const [actionObs, setActionObs] = useState('');
  const [rendirOpen, setRendirOpen] = useState(false);
  const [addGuiasOpen, setAddGuiasOpen] = useState(false);
  const [montoRendido, setMontoRendido] = useState('');
  const [rendicionObs, setRendicionObs] = useState('');
  const [liquidarOpen, setLiquidarOpen] = useState(false);
  const [adicionales, setAdicionales] = useState('0');
  const [descuentos, setDescuentos] = useState('0');
  const montoRef = useRef(null);
  const adicionalesRef = useRef(null);

  const [detalleQ, reexecute] = useQuery({
    query: REPARTO_DETALLE,
    variables: { id: repartoId },
    requestPolicy: 'network-only',
    pause: !repartoId,
  });
  const [retPendResult] = useQuery({
    query: RETORNABLES_EN_CIRCULACION,
    variables: { sucursalId: Number(sucursal?.id || 1) },
    pause: !sucursal?.id,
  });
  const [, iniciar] = useMutation(INICIAR_REPARTO);
  const [, registrarEntrega] = useMutation(REGISTRAR_ENTREGA);
  const [, registrarRetencion] = useMutation(REGISTRAR_RETENCION);
  const [, cerrar] = useMutation(CERRAR_REPARTO);
  const [, asignarGuias] = useMutation(ASIGNAR_GUIAS_REPARTO);
  const [, rendir] = useMutation(REGISTRAR_RENDICION);
  const [, aprobar] = useMutation(APROBAR_RENDICION);
  const [, liquidar] = useMutation(GENERAR_LIQUIDACION);
  const [, pagar] = useMutation(MARCAR_LIQUIDACION_PAGADA);

  const reparto = detalleQ.data?.repartoDetalle;
  const [choferesResult] = useQuery({ query: CHOFERES_LIST, variables: { page: 1, pageSize: 200 }, pause: !reparto?.chofer_id });
  const [vehiculos, setVehiculos] = useState([]);
  const repartoSucursalId = reparto?.sucursal_id;
  const hasUnidad = Boolean(reparto?.unidad_id);
  useEffect(() => {
    if (!repartoSucursalId || !hasUnidad) return undefined;
    let ignore = false;
    fetchVehiculos({ sucursal_id: repartoSucursalId, limit: 200 })
      .then((rows) => { if (!ignore) setVehiculos(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!ignore) setVehiculos([]); });
    return () => { ignore = true; };
  }, [repartoSucursalId, hasUnidad]);
  const chofer = (choferesResult.data?.choferes?.rows || []).find((item) => String(item.id) === String(reparto?.chofer_id));
  const unidad = vehiculos.find((item) => String(item.id) === String(reparto?.unidad_id));
  const choferLabel = chofer ? (chofer.nombreCompleto || [chofer.apellido, chofer.nombre].filter(Boolean).join(', ')) : reparto?.chofer_id;
  const unidadLabel = unidad ? [unidad.patente, unidad.modelo].filter(Boolean).join(' · ') : reparto?.unidad_id;
  const guias = useMemo(() => reparto?.guias || [], [reparto]);
  const rendiciones = useMemo(() => reparto?.rendiciones || [], [reparto]);
  const liquidaciones = useMemo(() => reparto?.liquidaciones || [], [reparto]);
  const actions = useMemo(
    () => getRepartoActions(reparto?.estado, code => hasPermission(user, code), { rendiciones, liquidaciones }),
    [reparto?.estado, user, rendiciones, liquidaciones],
  );
  const pendingRendicion = rendiciones.find(item => item.estado === 'pendiente');
  const pendingLiquidacion = liquidaciones.find(item => item.estado === 'pendiente');

  const refresh = () => reexecute({ requestPolicy: 'network-only' });

  const run = async (fn, fallback) => {
    setError('');
    setBusy(true);
    try {
      const result = await fn();
      if (result?.error) {
        setError(result.error.graphQLErrors?.[0]?.message || result.error.message || fallback);
        return false;
      }
      refresh();
      return true;
    } catch (err) {
      setError(err?.message || fallback);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openAction = (type, guia) => {
    if (actions.locked) {
      setError(`Acción bloqueada: el reparto está ${reparto.estado} y no admite nuevas entregas.`);
      return;
    }
    setError('');
    setActionText('');
    setActionObs('');
    setAction({ type, guia });
  };

  const submitAction = async event => {
    event.preventDefault();
    if (!action?.guia) return;
    const text = actionText.trim();
    if (!text) {
      setError(action.type === 'entrega' ? 'Ingrese el receptor.' : 'Ingrese el motivo.');
      return;
    }
    const repartoGuiaId = Number(action.guia.id);
    const ok = await run(
      () => (action.type === 'entrega'
        ? registrarEntrega({ repartoGuiaId, receptor: text, observaciones: actionObs.trim() || null })
        : registrarRetencion({ repartoGuiaId, motivo: text })),
      'No se pudo registrar la acción.',
    );
    if (ok) setAction(null);
  };

  const openRendir = () => {
    setError('');
    setMontoRendido(String(reparto.total_contrarrembolso ?? 0));
    setRendicionObs('');
    setRendirOpen(true);
  };

  const openLiquidar = () => {
    setError('');
    setAdicionales('0');
    setDescuentos('0');
    setLiquidarOpen(true);
  };

  const rendicionCheck = reparto
    ? validateRendicion({ declarado: reparto.total_contrarrembolso, rendido: montoRendido, observaciones: rendicionObs })
    : null;
  const liquidacionCheck = reparto
    ? validateLiquidacion({ flete: reparto.total_flete, adicionales, descuentos })
    : null;

  const submitRendicion = async event => {
    event.preventDefault();
    if (!rendicionCheck.ok) {
      setError(rendicionCheck.error);
      return;
    }
    const ok = await run(() => rendir({
      input: {
        reparto_id: repartoId,
        sucursal_id: reparto.sucursal_id,
        tipo: 'contrarrembolso',
        monto_declarado: Number(reparto.total_contrarrembolso || 0),
        monto_rendido: Number(String(montoRendido).trim().replace(',', '.')),
        observaciones: rendicionObs.trim() || null,
      },
    }), 'No se pudo registrar la rendición.');
    if (ok) setRendirOpen(false);
  };

  const submitLiquidacion = async event => {
    event.preventDefault();
    if (!liquidacionCheck.ok) {
      setError(liquidacionCheck.error);
      return;
    }
    const ok = await run(() => liquidar({
      input: {
        reparto_id: repartoId,
        sucursal_id: reparto.sucursal_id,
        total_adicionales: liquidacionCheck.adicionales,
        total_descuentos: liquidacionCheck.descuentos,
      },
    }), 'No se pudo generar la liquidación.');
    if (ok) setLiquidarOpen(false);
  };

  const printPlanilla = async () => {
    setError('');
    try {
      await openRepartoPlanilla(repartoId);
    } catch (err) {
      setError(err.message);
    }
  };

  if (!reparto) {
    return (
      <div className="mx-auto max-w-[1400px] p-4 text-[rgb(var(--muted))]">
        {detalleQ.fetching ? 'Cargando...' : detalleQ.error?.message || 'Reparto no encontrado.'}
      </div>
    );
  }

  const bloqueadoMensaje = actions.locked
    ? `Acción bloqueada: el reparto está ${reparto.estado} y no admite nuevas guías ni entregas.`
    : '';
  const panel = 'rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4';
  const tabs = ['guias', 'rendicion', 'liquidacion'];

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 px-4 py-4">
      <header className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4">
        <div>
          <h1 className="text-2xl font-semibold text-[rgb(var(--fg))]">Reparto #{reparto.id}</h1>
          <p className="text-sm text-[rgb(var(--muted))]">
            Estado: <span className="rounded-full border border-[rgb(var(--border))] px-2 py-1">{reparto.estado}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {actions.iniciar ? (
            <Button size="sm" disabled={busy} onClick={() => run(() => iniciar({ id: repartoId }), 'No se pudo iniciar el reparto.')}>
              Iniciar reparto
            </Button>
          ) : null}
          {canAddGuias(reparto.estado, code => hasPermission(user, code)) ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setAddGuiasOpen(true)}>Agregar guías</Button>
          ) : null}
          <Button size="sm" variant="outline" onClick={printPlanilla}>Imprimir planilla</Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy || !actions.cerrar}
            onClick={() => run(() => cerrar({ id: repartoId }), 'No se pudo cerrar el reparto.')}
          >
            Cerrar reparto
          </Button>
          <Link to="/cargas/repartos" className="rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm">Volver</Link>
        </div>
      </header>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      ) : null}
      {bloqueadoMensaje ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{bloqueadoMensaje}</div>
      ) : null}
      {(retPendResult.data?.retornablesEnCirculacion || []).some(item => Number(item.repartoId) === repartoId) ? (
        <div className={`${panel} text-sm`}>
          Retornables pendientes para este reparto.{' '}
          <Link className="underline" to={`/cargas/documentacion-por-bolsa?contexto=reparto&id=${id}`}>Ir a rendición de retornables</Link>
        </div>
      ) : null}

      <section className={`${panel} grid gap-4 text-sm text-[rgb(var(--muted))] lg:grid-cols-2`}>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase">Datos del reparto</p>
          <p>Fecha: {formatDate(reparto.fecha)}</p>
          <p>Chofer: {choferLabel || '-'}</p>
          <p>Unidad: {unidadLabel || '-'}</p>
          <p>Guías: {reparto.total_guias} · Contrarreembolso: {formatCurrency(reparto.total_contrarrembolso)}</p>
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase">Observaciones</p>
          <p>{reparto.observaciones || 'Sin observaciones.'}</p>
        </div>
      </section>

      <nav className="flex gap-2">
        {tabs.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${tab === t ? 'bg-[rgb(var(--brand))] text-white' : 'border border-[rgb(var(--border))] text-[rgb(var(--fg))]'}`}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === 'guias' ? (
        <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))]">
          <div className="border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm text-amber-800">{RETIRO_BLOQUEADO_MENSAJE}.</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase text-[rgb(var(--muted))]">
                <tr>
                  <th className="px-4 py-3 text-left">Guía</th>
                  <th className="px-4 py-3 text-left">Destinatario</th>
                  <th className="px-4 py-3 text-left">Domicilio</th>
                  <th className="px-4 py-3 text-left">Localidad</th>
                  <th className="px-4 py-3 text-right">Bultos</th>
                  <th className="px-4 py-3 text-right">Contrarreembolso</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {guias.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-[rgb(var(--muted))]">No hay guías asignadas.</td></tr>
                ) : guias.map(guia => {
                  const pendiente = canActOnGuia(guia.estado);
                  const blockReason = (permission) => guiaActionBlockReason({
                    guiaEstado: guia.estado, repartoEstado: reparto?.estado, permission, can: code => hasPermission(user, code),
                  });
                  return (
                    <tr key={guia.id} className="border-t border-[rgb(var(--border))]">
                      <td className="px-4 py-3 font-medium">{guia.numero_guia || `#${guia.guia_id}`}</td>
                      <td className="px-4 py-3">{guia.destinatario || '-'}</td>
                      <td className="px-4 py-3">{guia.domicilio || '-'}</td>
                      <td className="px-4 py-3">{guia.localidad || '-'}</td>
                      <td className="px-4 py-3 text-right">{guia.bultos ?? '-'}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(guia.contrarreembolso)}</td>
                      <td className="px-4 py-3">
                        {guia.estado}
                        {guia.receptor ? <span className="block text-xs text-[rgb(var(--muted))]">Recibió: {guia.receptor}</span> : null}
                        {guia.novedad ? <span className="block text-xs text-[rgb(var(--muted))]">{guia.novedad}</span> : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button size="sm" variant="success" disabled={busy || !pendiente || !actions.entregar} title={blockReason('REPARTOS_REGISTRAR_ENTREGA')} onClick={() => openAction('entrega', guia)}>
                            Entregar
                          </Button>
                          <Button size="sm" variant="outline" disabled={busy || !pendiente || !actions.retener} title={blockReason('REPARTOS_REGISTRAR_NOVEDAD')} onClick={() => openAction('retencion', guia)}>
                            Retener
                          </Button>
                          <Button asChild size="sm" variant="outline">
                            <Link to={`/cargas/retornables/nuevo?repartoId=${id}&guiaId=${guia.guia_id}`}>Retornables</Link>
                          </Button>
                          <Button size="sm" variant="outline" disabled title={RETIRO_BLOQUEADO_MENSAJE}>Retiro</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {tab === 'rendicion' ? (
        <section className={panel}>
          <h2 className="text-lg font-semibold">Rendición</h2>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              disabled={busy || !actions.rendir}
              onClick={openRendir}
            >
              Rendir contrarreembolso
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !actions.aprobar || !pendingRendicion}
              onClick={() => run(() => aprobar({ id: Number(pendingRendicion.id) }), 'No se pudo aprobar la rendición.')}
            >
              Aprobar rendición
            </Button>
          </div>
          <ul className="mt-3 space-y-2 text-sm">
            {rendiciones.map(r => (
              <li key={r.id} className="rounded-xl border border-[rgb(var(--border))] p-3">
                {r.tipo} · declarado {formatCurrency(r.monto_declarado)} · rendido {formatCurrency(r.monto_rendido)} · diferencia {formatCurrency(r.diferencia)} · {r.estado}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {tab === 'liquidacion' ? (
        <section className={panel}>
          <h2 className="text-lg font-semibold">Liquidación chofer/fletero</h2>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              disabled={busy || !actions.liquidar}
              onClick={openLiquidar}
            >
              Generar liquidación
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !actions.pagar || !pendingLiquidacion}
              onClick={() => run(() => pagar({ id: Number(pendingLiquidacion.id) }), 'No se pudo marcar la liquidación como pagada.')}
            >
              Marcar pagada
            </Button>
          </div>
          <ul className="mt-3 space-y-2 text-sm">
            {liquidaciones.map(l => (
              <li key={l.id} className="rounded-xl border border-[rgb(var(--border))] p-3">
                {l.tipo} · total final {formatCurrency(l.total_final)} · {l.estado}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <AgregarGuiasModal
        isOpen={addGuiasOpen}
        onClose={() => setAddGuiasOpen(false)}
        sucursalId={reparto.sucursal_id}
        excludedGuiaIds={guias.map(item => item.guia_id)}
        busy={busy}
        onConfirm={async guiaIds => {
          const ok = await run(() => asignarGuias({ repartoId, guiaIds }), 'No se pudieron agregar las guías.');
          if (ok) setAddGuiasOpen(false);
        }}
      />
      <ModalBase
        isOpen={Boolean(action)}
        onClose={() => setAction(null)}
        labelledBy="modal-accion-guia-reparto"
        initialFocusRef={receptorRef}
        className="modal modal-content w-full max-w-md rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-5"
      >
        {action ? (
          <form onSubmit={submitAction} className="space-y-3">
            <h2 id="modal-accion-guia-reparto" className="text-lg font-semibold">
              {action.type === 'entrega' ? 'Registrar entrega' : 'Retener guía'} · {action.guia.numero_guia || `#${action.guia.guia_id}`}
            </h2>
            <label className="block text-sm">
              {action.type === 'entrega' ? 'Receptor' : 'Motivo'}
              <Input ref={receptorRef} value={actionText} onChange={event => setActionText(event.target.value)} />
            </label>
            {action.type === 'entrega' ? (
              <label className="block text-sm">
                Observaciones
                <Textarea value={actionObs} onChange={event => setActionObs(event.target.value)} />
              </label>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setAction(null)}>Cancelar</Button>
              <Button type="submit" size="sm" disabled={busy}>Confirmar</Button>
            </div>
          </form>
        ) : null}
      </ModalBase>

      <ModalBase
        isOpen={rendirOpen}
        onClose={() => setRendirOpen(false)}
        labelledBy="modal-rendir-reparto"
        initialFocusRef={montoRef}
        className="modal modal-content w-full max-w-md rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-5"
      >
        {rendirOpen ? (
          <form onSubmit={submitRendicion} className="space-y-3">
            <h2 id="modal-rendir-reparto" className="text-lg font-semibold">Rendir contrarreembolso · Reparto #{reparto.id}</h2>
            <p className="text-sm text-[rgb(var(--muted))]">Monto declarado: {formatCurrency(reparto.total_contrarrembolso)}</p>
            <label className="block text-sm">
              Monto rendido
              <Input ref={montoRef} inputMode="decimal" value={montoRendido} onChange={event => setMontoRendido(event.target.value)} />
            </label>
            <p className="text-sm">
              Diferencia: <strong>{formatCurrency(calcDiferencia(reparto.total_contrarrembolso, montoRendido) || 0)}</strong>
            </p>
            <label className="block text-sm">
              Observaciones{rendicionCheck.diferencia !== 0 ? ' (obligatorias)' : ''}
              <Textarea value={rendicionObs} onChange={event => setRendicionObs(event.target.value)} />
            </label>
            {rendicionCheck.ok ? null : <p className="text-sm text-red-600">{rendicionCheck.error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setRendirOpen(false)}>Cancelar</Button>
              <Button type="submit" size="sm" disabled={busy || !rendicionCheck.ok}>Confirmar</Button>
            </div>
          </form>
        ) : null}
      </ModalBase>

      <ModalBase
        isOpen={liquidarOpen}
        onClose={() => setLiquidarOpen(false)}
        labelledBy="modal-liquidar-reparto"
        initialFocusRef={adicionalesRef}
        className="modal modal-content w-full max-w-md rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-5"
      >
        {liquidarOpen ? (
          <form onSubmit={submitLiquidacion} className="space-y-3">
            <h2 id="modal-liquidar-reparto" className="text-lg font-semibold">Generar liquidación · Reparto #{reparto.id}</h2>
            <p className="text-sm text-[rgb(var(--muted))]">Total flete: {formatCurrency(reparto.total_flete)}</p>
            <label className="block text-sm">
              Adicionales
              <Input ref={adicionalesRef} inputMode="decimal" value={adicionales} onChange={event => setAdicionales(event.target.value)} />
            </label>
            <label className="block text-sm">
              Descuentos
              <Input inputMode="decimal" value={descuentos} onChange={event => setDescuentos(event.target.value)} />
            </label>
            <p className="text-sm">
              Total final: <strong>{formatCurrency(calcTotalFinal(reparto.total_flete, adicionales, descuentos))}</strong>
            </p>
            {liquidacionCheck.ok ? null : <p className="text-sm text-red-600">{liquidacionCheck.error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setLiquidarOpen(false)}>Cancelar</Button>
              <Button type="submit" size="sm" disabled={busy || !liquidacionCheck.ok}>Confirmar</Button>
            </div>
          </form>
        ) : null}
      </ModalBase>
    </div>
  );
}
